import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type KeyboardEvent,
} from "react";
import { Terminal, Copy, Check, PencilSimple, X } from "@/components/ui/icons";
import { useZero } from "@rocicorp/zero/react";
import { cn } from "@/lib/utils";
import { mutators } from "@/lib/mutators";
import { CardShell } from "./CardShell";

// Matches URLs and GitHub-style owner/repo#123 references
const LINK_RE = /https?:\/\/[^\s)>\]]+|(?<![/\w])[\w.-]+\/[\w.-]+#(\d+)(?!\w)/g;

/** Replace URLs and GitHub refs in text with clickable links. */
function linkify(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  // Reset lastIndex since the regex is reused across calls
  LINK_RE.lastIndex = 0;
  while ((match = LINK_RE.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const raw = match[0];
    // GitHub owner/repo#123 → full URL
    const isGhRef = match[1] !== undefined;
    const href = isGhRef
      ? `https://github.com/${raw.replace(/#(\d+)$/, "/issues/$1")}`
      : raw;
    parts.push(
      <a
        key={match.index}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-info underline break-all hover:text-info/80"
      >
        {raw}
      </a>,
    );
    last = match.index + raw.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

interface TerminalOutputCardProps {
  content: string;
  cwd?: string;
  compact?: boolean;
  /** When set together with `canEdit`, enables in-card editing for the sender. */
  messageId?: string;
  canEdit?: boolean;
  /** Full payload — preserved across edit so passthrough fields aren't lost. */
  payload?: Record<string, unknown> | null;
}

export function TerminalOutputCard({
  content,
  cwd,
  compact,
  messageId,
  canEdit,
  payload,
}: TerminalOutputCardProps) {
  const zero = useZero();
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftContent, setDraftContent] = useState(content);
  const [draftCwd, setDraftCwd] = useState(cwd ?? "");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const editable = Boolean(canEdit && messageId);

  useEffect(() => {
    if (!isEditing) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [isEditing]);

  function handleCopy() {
    navigator.clipboard
      .writeText(content)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  }

  function enterEdit() {
    setDraftContent(content);
    setDraftCwd(cwd ?? "");
    setIsEditing(true);
  }

  function cancelEdit() {
    // Drafts are reseeded from props in enterEdit(), so there's no need to
    // reset them here — the textarea/input unmount the moment isEditing flips.
    setIsEditing(false);
  }

  function saveEdit() {
    if (!messageId) return;
    const nextCwd = draftCwd.trim();
    const nextPayload: Record<string, unknown> = {
      ...(payload ?? {}),
      content: draftContent,
    };
    if (nextCwd) nextPayload.cwd = nextCwd;
    else delete nextPayload.cwd;
    zero.mutate(
      mutators.actions.editMessage({
        messageId,
        payload: nextPayload,
      }),
    );
    setIsEditing(false);
  }

  function handleTextareaKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    } else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      saveEdit();
    }
  }

  const actionButtonClass = cn(
    "flex items-center gap-1 rounded px-1.5 py-0.5",
    "text-[10px] text-muted-foreground",
    "hover:bg-foreground/10 transition-colors",
  );

  const headerActions = isEditing ? (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={cancelEdit}
        className={actionButtonClass}
        aria-label="Cancel edit"
      >
        <X size={10} />
        Cancel
      </button>
      <button
        type="button"
        onClick={saveEdit}
        className={cn(
          actionButtonClass,
          "text-foreground hover:bg-foreground/15",
        )}
        aria-label="Save edit"
      >
        <Check size={10} className="text-success" />
        Save
      </button>
    </div>
  ) : (
    <div className="flex items-center gap-1">
      <button type="button" onClick={handleCopy} className={actionButtonClass}>
        {copied ? (
          <Check size={10} className="text-success" />
        ) : (
          <Copy size={10} />
        )}
        {copied ? "Copied" : "Copy"}
      </button>
      {editable && (
        <button
          type="button"
          onClick={enterEdit}
          className={actionButtonClass}
          aria-label="Edit terminal card"
        >
          <PencilSimple size={10} />
          Edit
        </button>
      )}
    </div>
  );

  const label = isEditing ? (
    <span className="inline-flex items-center gap-1">
      <span>terminal —</span>
      <input
        type="text"
        value={draftCwd}
        onChange={(e) => setDraftCwd(e.target.value)}
        placeholder="cwd"
        className={cn(
          "min-w-[4rem] max-w-[16rem] rounded px-1 py-0",
          "bg-foreground/5 text-[10px] text-foreground/90",
          "border border-transparent focus-visible:border-border focus-visible:outline-none",
          "placeholder:text-muted-foreground/50",
        )}
      />
    </span>
  ) : (
    <>{cwd ? `terminal — ${cwd}` : "terminal"}</>
  );

  return (
    <CardShell
      compact={compact}
      icon={<Terminal size={12} className="text-muted-foreground" />}
      label={label}
      labelUppercase={false}
      telemetryCardType="terminal-output"
      headerActions={headerActions}
      contentClassName="px-2 py-1.5"
    >
      {isEditing ? (
        <textarea
          ref={textareaRef}
          value={draftContent}
          onChange={(e) => setDraftContent(e.target.value)}
          onKeyDown={handleTextareaKeyDown}
          rows={Math.min(16, Math.max(3, draftContent.split("\n").length))}
          className={cn(
            "w-full resize-y max-h-[400px]",
            "rounded bg-foreground/5 px-2 py-1.5",
            "text-[11px] leading-relaxed text-foreground/90",
            "font-mono whitespace-pre-wrap break-words",
            "border border-transparent focus-visible:border-border focus-visible:outline-none",
          )}
          aria-label="Terminal output"
        />
      ) : (
        <pre
          className={cn(
            "max-h-[200px] overflow-y-auto overflow-x-auto",
            "rounded bg-foreground/5 px-2 py-1.5",
            "text-[11px] leading-relaxed text-foreground/90",
            "font-mono whitespace-pre-wrap break-words",
          )}
        >
          <code>{linkify(content)}</code>
        </pre>
      )}
      {isEditing && (
        <p className="mt-1 text-[10px] text-muted-foreground/50">
          ⌘↵ to save · Esc to cancel
        </p>
      )}
    </CardShell>
  );
}
